'use client';
import { Button } from '@curology/ui-components-web/react/Button';
import { Checkbox } from '@curology/ui-components-web/react/Checkbox';
import { Highlight } from '@curology/ui-components-web/react/Highlight';
import {
  MaskedInput,
  type MaskedInputProps,
} from '@curology/ui-components-web/react/MaskedInput';
import {
  TextInput,
  type TextInputProps,
} from '@curology/ui-components-web/react/TextInput';
import React, { useEffect, useState } from 'react';
import { type QuestionTextModel } from 'survey-core';

import { useFlowContext } from '@/components/flow/hooks/useFlowContext';
import LegalLinks from '@/components/legalLinks';
import { LegalModal } from '@/components/modules/legalModal';
import { BRAND_IDS } from '@/consts/brands';
import { TreatmentCategoryIds } from '@/consts/treatmentCategories';
import { PhoneNumberVerificationService } from '@/services/phoneNumberVerificationService/client';
import { UserService } from '@/services/userService/client';
import { type AnalyticsPatientResource } from '@/types/metrics/analyticsPatient';
import { trackEvent, AnalyticsEvent } from '@/utils/analytics';

import BackButton from '../buttons/back';

const DEFAULT_TITLE = 'What’s your phone number?';
const DEFAULT_VERIFICATION_TITLE = 'Verify your phone number';

const PhoneNumberVerification = ({
  question,
}: {
  question: QuestionTextModel;
}) => {
  const { treatmentCategory, brand } = useFlowContext();
  const brandId = BRAND_IDS[brand];
  const [isPhoneVerificationRequired, setIsPhoneVerificationRequired] =
    useState(false);
  const [isEditingPhoneNumber, setIsEditingPhoneNumber] = useState(true);
  const [isReceivingTexts, setIsReceivingTexts] = useState(false);
  const [isDisabled, setIsDisabled] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [errorState, setErrorState] = useState<MaskedInputProps['state']>('');
  const [validationCodeErrorState, setValidationCodeErrorState] =
    useState<TextInputProps['state']>('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [patient, setPatient] = useState<
    AnalyticsPatientResource | undefined
  >();

  const inSkinFlowAndVerifyingPhoneNumber =
    treatmentCategory === 'skin' && !isEditingPhoneNumber;

  useEffect(() => {
    question.title = question.getPropertyValue('title') ?? DEFAULT_TITLE;
    const checkIfPhoneVerificationRequired = async () => {
      const [phoneVerificationRequired, patientData] = await Promise.all([
        PhoneNumberVerificationService.getIsPhoneNumberVerificationRequired(
          brand,
        ),
        UserService.getPatient(brand),
      ]);

      setIsPhoneVerificationRequired(phoneVerificationRequired);
      setIsLoading(false);
      setPatient(patientData);
    };
    checkIfPhoneVerificationRequired().catch(console.error);
  }, [question, brand]);

  useEffect(() => {
    if (verificationCode.length === 0) {
      return;
    }
    const validateVerificationCode = () =>
      PhoneNumberVerificationService.postConfirmationCode(
        brand,
        verificationCode,
      );

    const timeOutId = setTimeout(
      () =>
        validateVerificationCode()
          .then(() => {
            trackEvent(AnalyticsEvent.SMS_VERIFICATION_PAGE_COMPLETED, {
              brandId,
              treatmentCategory: treatmentCategory!,
              treatmentCategoryId: TreatmentCategoryIds[treatmentCategory!],
              userId: patient?.id,
            });

            setValidationCodeErrorState('success');
            setIsDisabled(false);

            question.surveyModel.nextPage() ||
              question.surveyModel.completeLastPage();
          })
          .catch(() => {
            setValidationCodeErrorState('critical');
          }),
      500,
    );
    return () => clearTimeout(timeOutId);
  }, [
    question.surveyModel,
    treatmentCategory,
    verificationCode,
    patient,
    brand,
    brandId,
  ]);

  // @ts-expect-error - TODO: broken by noImplicitAny
  const handleOnChange = (event) => {
    question.value = event.target.value;
    setPhoneNumber(event.target.value);
    const isNotValid =
      event.target.value.replaceAll('-', '').trim().length < 10;
    setIsDisabled(isNotValid);
  };

  // @ts-expect-error - TODO: broken by noImplicitAny
  const handleOnBlur = (event) => {
    const isNotValid =
      event.target.value.replaceAll('-', '').trim().length < 10;
    setErrorState(isNotValid ? 'critical' : '');
  };

  const handleOnFocus = () => {
    setErrorState('');
  };

  const handleOnCheckboxChange = () => {
    setIsReceivingTexts(!isReceivingTexts);
  };

  // @ts-expect-error - TODO: broken by noImplicitAny
  const handleVerificationCodeOnChange = (event) => {
    setVerificationCode(event.target.value);
  };

  const handleOnClick = async () => {
    if (isPhoneVerificationRequired && isEditingPhoneNumber) {
      await PhoneNumberVerificationService.postPhoneNumberToTextConfirmationCode(
        brand,
        question.value,
      );
      setIsEditingPhoneNumber(false);
      setIsDisabled(true);
      question.title =
        question.getPropertyValue('phoneVerificationTitle') ??
        DEFAULT_VERIFICATION_TITLE;
    } else {
      trackEvent(AnalyticsEvent.SMS_VERIFICATION_PAGE_COMPLETED, {
        brandId,
        treatmentCategory: treatmentCategory!,
        treatmentCategoryId: TreatmentCategoryIds[treatmentCategory!],
        userId: patient?.id,
      });

      question.surveyModel.nextPage() ||
        question.surveyModel.completeLastPage();
    }
  };

  const handleVerifyPhoneNumberPageBackOnClickForSkin = () => {
    question.title = question.getPropertyValue('title') ?? DEFAULT_TITLE;
    question.value = phoneNumber;

    setIsEditingPhoneNumber(true);
    setIsDisabled(false);
    setValidationCodeErrorState('');
  };

  return (
    <>
      {isEditingPhoneNumber ? (
        <>
          {isPhoneVerificationRequired ? (
            <div className="-cur-mt-4 cur-mb-3 cur-font-body cur-text-body cur-tracking-body cur-leading-6 cur-text-subtle cur-whitespace-pre-line">
              We’ll text you a one-time verification code to make sure you’re
              really you.
            </div>
          ) : (
            <div className="-cur-mt-4 cur-mb-3 cur-font-body cur-text-body cur-tracking-body cur-leading-6 cur-text-subtle cur-whitespace-pre-line">
              Enter your phone number to receive text updates including order
              confirmations, shipment updates, and messages from your provider.
            </div>
          )}
          <div role="presentation" className="cur-pb-3">
            <MaskedInput
              label="Phone number"
              onChange={handleOnChange}
              onBlur={handleOnBlur}
              onFocus={handleOnFocus}
              state={errorState}
              helperText={
                errorState === 'critical'
                  ? 'Please enter a valid phone number with 10 digits.'
                  : ''
              }
              placeholder="123-456-7890"
              mask={[
                /[0-9]/,
                /[0-9]/,
                /[0-9]/,
                '-',
                /[0-9]/,
                /[0-9]/,
                /[0-9]/,
                '-',
                /[0-9]/,
                /[0-9]/,
                /[0-9]/,
                /[0-9]/,
              ]}
              value={phoneNumber}
            />
          </div>
          <div className="cur-border-t cur-border-solid cur-mt-1 cur-pt-4 cur-mb-4">
            <Checkbox
              id="get-texts"
              checked={isReceivingTexts}
              onClick={handleOnCheckboxChange}
              tabIndex={0}
              label="Get texts about your account, shipments, and Curology news"
              size="sm"
            />
          </div>
          <Highlight className="cur-leading-6" size="sm">
            By checking the box above, I agree to receive transactional and/or
            marketing texts from Curology at the number provided. Opting in is
            always optional. Message frequency varies. Reply HELP for help, or
            STOP to cancel. Messages may be automated. Message and data rates
            may apply.
          </Highlight>
        </>
      ) : (
        <>
          <div className="-cur-mt-4 cur-mb-3 cur-font-body cur-text-body cur-tracking-body cur-leading-6 cur-text-subtle cur-whitespace-pre-line">
            We just texted you! Enter the code you received below.
          </div>
          <div role="presentation" className="cur-pb-3">
            <TextInput
              helperText="You'll receive an SMS to your phone."
              label="Verification code"
              onChange={handleVerificationCodeOnChange}
              state={validationCodeErrorState}
            />
          </div>
        </>
      )}
      <div className="cur-flex cur-flex-col cur-gap-y-2 cur-mt-6">
        <Button
          onClick={handleOnClick}
          className="cur-w-full"
          disabled={isDisabled}
          loading={isLoading}
        >
          {isEditingPhoneNumber ? 'Next' : 'Verify'}
        </Button>
        {(!question.hideBackNavigation ||
          inSkinFlowAndVerifyingPhoneNumber) && (
          <BackButton
            model={question.surveyModel}
            isFirstPage={false}
            // @ts-expect-error - TODO: broken by strictNullChecks
            onFirstPageBack={null}
            goBackToEditingPhoneNumber={inSkinFlowAndVerifyingPhoneNumber}
            handleVerifyPhoneNumberPageBackOnClickForSkin={
              handleVerifyPhoneNumberPageBackOnClickForSkin
            }
          />
        )}
      </div>
      <div className="cur-text-center cur-gap-y-2 cur-mt-4">
        <LegalLinks.TermsOfService brand={brand} />
        {'  |  '}
        <LegalLinks.PrivacyPolicy brand={brand} />
      </div>
      <LegalModal />
    </>
  );
};

export default PhoneNumberVerification;
